import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import ProTipService from '../../../services/proTipService';

const EditTipModal = ({ isOpen, onClose, onTipUpdated, tip, category, user, onError }) => {
  const [formData, setFormData] = useState({
    title: '',
    content: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Initialize form data when tip prop changes
  useEffect(() => {
    if (tip) {
      setFormData({
        title: tip?.title || '',
        content: tip?.content || ''
      });
    }
  }, [tip]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.title?.trim()) {
      newErrors.title = 'Tip title is required';
    }

    if (!formData?.content?.trim()) {
      newErrors.content = 'Tip content is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setIsLoading(true);
      onError?.(null);

      const { data, error } = await ProTipService?.updateProTip(tip?.id, {
        title: formData?.title?.trim(),
        content: formData?.content?.trim(),
        updated_by: user?.id
      });

      if (error) {
        onError?.(`Failed to update tip: ${error}`);
        return;
      }

      // Success
      onTipUpdated?.();
    } catch (err) {
      onError?.('An unexpected error occurred while updating the tip.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
        {/* Modal Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                <Icon name="Edit2" size={16} className="text-white" />
              </div>
              <div>
                <h3 className="text-lg font-heading font-semibold">
                  Edit Tip
                </h3>
                <p className="text-blue-100 text-sm">
                  {category?.label} category
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              onClick={onClose}
              iconName="X"
              className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg"
            />
          </div>
        </div>

        {/* Modal Content */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-6">
            {/* Title Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tip Title *
              </label>
              <Input
                type="text"
                placeholder="Enter a clear, concise title for this tip..."
                value={formData?.title}
                onChange={(e) => handleInputChange('title', e?.target?.value)}
                className={`w-full rounded-lg ${errors?.title ? 'border-red-500' : ''}`}
                maxLength={100}
              />
              {errors?.title && (
                <p className="text-red-500 text-xs mt-1">{errors?.title}</p>
              )}
              <p className="text-gray-500 text-xs mt-1">
                {formData?.title?.length}/100 characters
              </p>
            </div>

            {/* Content Field */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tip Content *
              </label>
              <textarea
                placeholder="Provide detailed guidance, advice, or instructions..."
                value={formData?.content}
                onChange={(e) => handleInputChange('content', e?.target?.value)}
                rows={6}
                className={`
                  w-full px-3 py-2 border border-gray-300 rounded-lg resize-none 
                  focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
                  ${errors?.content ? 'border-red-500' : ''}
                `}
                maxLength={1000}
              />
              {errors?.content && (
                <p className="text-red-500 text-xs mt-1">{errors?.content}</p>
              )}
              <p className="text-gray-500 text-xs mt-1">
                {formData?.content?.length}/1000 characters
              </p>
            </div>

            {/* Tip Info */}
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <Icon name="Info" size={16} className="text-blue-600" />
                <p className="text-sm text-blue-800">
                  Last updated: {new Date(tip?.updated_at || tip?.created_at)?.toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end space-x-3 pt-6">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isLoading}
              className="rounded-lg"
            >
              Cancel
            </Button>

            <Button
              type="submit"
              variant="default"
              disabled={isLoading || !formData?.title?.trim() || !formData?.content?.trim()}
              iconName="Save"
              iconPosition="left"
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
            >
              {isLoading ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditTipModal;